import type { BlockPartnersLocation } from 'types/block-partners-location';
import React, { memo } from 'react';
import Head from 'next/head';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useShareImages } from 'hooks/useShareImages';

type LocationBusinessProps = Pick<BlockPartnersLocation, 'partners'>;

const LocationBusinessJson: React.FC<LocationBusinessProps> = memo(
  ({ partners }) => {
    const { seoSettings } = useWordPressContext();
    const leafwellImage = useShareImages({ type: 'full', title: '' });
    const departments = partners.map(
      ({
        name,
        internationalPhoneNumber,
        streetAddress,
        addressLocality,
        addressRegion,
        postalCode,
        addressCountry,
        latitude,
        longitude,
        priceRange,
      }) => {
        return {
          '@type': 'LocalBusiness',
          name,
          telephone: internationalPhoneNumber,
          priceRange: [...Array(priceRange).keys()].map(_ => '$').join(''),
          address: {
            '@type': 'PostalAddress',
            streetAddress,
            addressLocality,
            addressRegion,
            postalCode,
            addressCountry,
          },
          location: {
            '@type': 'Place',
            geo: {
              '@type': 'GeoCoordinates',
              latitude,
              longitude,
            },
          },
        };
      },
    );

    const details = {
      '@context': 'https://www.schema.org/',
      '@type': 'LocalBusiness',
      name: 'Leafwell - Main Office',
      url: 'https://www.leafwell.com/',
      '@id': 'https://www.leafwell.com/#LocalBusiness',
      logo: 'https://admin.leafwell.com/app/uploads/2021/10/leafwell-icon.jpg',
      image: leafwellImage.url,
      description: seoSettings.homepage.homepageDescription,
      telephone: '+18006609085',
      sameAs: [
        'https://www.facebook.com/leafwellofficial/',
        'https://twitter.com/_Leafwell_',
        'https://www.instagram.com/leafwell_official/',
        'https://www.linkedin.com/company/leafwellofficial/',
        'https://www.pinterest.com/leafwell_official/',
        'https://www.youtube.com/channel/UCok2ps-pUDEA885yqTaZLgA/',
      ],
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
            'Sunday',
          ],
          opens: '08:00',
          closes: '20:00',
        },
      ],
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '9100 South Dadeland Blvd, Suite 1701',
        addressLocality: 'Miami',
        addressRegion: 'FL',
        postalCode: '33156',
        addressCountry: 'USA',
      },
      location: {
        '@type': 'Place',
        geo: {
          '@type': 'GeoCoordinates',
          latitude: '25.68544396177287',
          longitude: '-80.31434903795771',
        },
      },
      department: departments,
    };

    return React.createElement(
      Head,
      null,
      React.createElement('script', {
        type: 'application/ld+json',
        dangerouslySetInnerHTML: {
          __html: JSON.stringify(details),
        },
      }),
    );
  },
);

export default LocationBusinessJson;
